import React from "react";
import { Chip, Stack, TextField, Typography } from "@mui/material";
import { IndividualTextColorControl } from "../inspector/LandingInspectorControls";
import { elementTextFallback, normalizeEditableColor } from "../inspector/inspectorModel";
import {
  buildElementColorVisualStyle,
  currentElementColor,
  effectiveSectionPalette,
  getValueAtDescriptorPath,
  sectionVisualPath,
} from "../elementEditing";

function appearanceTextFallback(form, descriptor, palette) {
  const sectionType = descriptor?.sectionType || descriptor?.sectionId || "";
  const appearance = form?.appearance?.[sectionType] || {};
  const path = String(descriptor?.fieldPath || descriptor?.canvasPath || "").toLowerCase();
  if (path.includes("eyebrow")) return appearance.accentColor || palette.primary;
  if (path.includes("title") || path.endsWith(".name")) return appearance.titleColor || palette.secondary || palette.text;
  if (path.includes("subtitle") || path.includes("description")) return appearance.textColor || palette.mutedText || palette.text;
  return elementTextFallback(path, palette, sectionType);
}

export default function TextElementEditor({ descriptor, form, disabled, onCommit }) {
  const field = descriptor?.field;
  const path = descriptor?.fieldPath || field?.path || "";
  const current = String(getValueAtDescriptorPath(form, path, "") ?? "");
  const [draft, setDraft] = React.useState(current);

  React.useEffect(() => setDraft(current), [current, path]);
  if (!field || !path) return null;

  const palette = effectiveSectionPalette(form, descriptor);
  const fallback = normalizeEditableColor(appearanceTextFallback(form, descriptor, palette)) || "#000000";
  const colorPath = descriptor?.canvasPath || path;
  const ownColor = currentElementColor(form, descriptor, colorPath);
  const visualPath = sectionVisualPath(descriptor);

  const commitText = () => {
    if (draft !== current) onCommit?.(path, draft);
  };
  const commitColor = (nextColor) => {
    if (!visualPath) return;
    onCommit?.(visualPath, buildElementColorVisualStyle(form, descriptor, colorPath, nextColor));
  };

  return (
    <Stack spacing={1.35}>
      <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
        <Chip size="small" label={ownColor ? "Color propio" : "Heredado"} variant={ownColor ? "filled" : "outlined"} />
      </Stack>
      <TextField
        fullWidth
        size="small"
        label={field.label || descriptor.label || "Texto"}
        value={draft}
        disabled={disabled}
        multiline={field.type === "textarea"}
        minRows={field.type === "textarea" ? 4 : undefined}
        inputProps={{ maxLength: field.maxLength }}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitText}
        onKeyDown={(event) => {
          if (!field.type?.includes?.("textarea") && event.key === "Enter") event.currentTarget.blur();
        }}
      />
      <IndividualTextColorControl
        fieldPath={colorPath}
        label={field.label || descriptor.label || "Texto"}
        value={ownColor}
        fallback={fallback}
        disabled={disabled}
        onChange={commitColor}
        onReset={() => commitColor("")}
      />
      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)" }}>
        Heredar elimina el color propio y vuelve a usar los colores de la sección.
      </Typography>
    </Stack>
  );
}
